import React, { useState, useEffect } from 'react';
import { Loader2, Check, Zap, Building2, CreditCard, Sparkles, Cpu, ShieldCheck, Image as ImageIcon } from 'lucide-react';
import { UserProfile } from '../types';
import { trackEvent } from '../services/analytics';
import { api } from '../services/api';
import { shouldRenderClerkPricingTable } from '../src/billing/flag';
import { ClerkPricingTable } from './ClerkPricingTable';

interface Plan {
  id: string;
  name: string;
  label: string;
  amount: string;
  firstAmount?: string;
  /** API returns renderQuota (Sprint 70+). `renders` kept for backwards compat. */
  renders?: number;
  renderQuota?: number;
  yearly: boolean;
  extraRenderPrice: string;
}

interface PlansResponse {
  currency: string;
  plans: Plan[];
}

interface SubStatus {
  active: boolean;
  plan: string | null;
  planLabel: string | null;
  rendersPerMonth: number;
  rendersUsed: number;
  periodEnd: string | null;
  cancelled: boolean;
}

interface Props {
  user: UserProfile;
}

export const SubscriptionPage: React.FC<Props> = ({ user }) => {
  // Phase 10-07b cutover seam — when VITE_USE_CLERK_BILLING is on, render
  // Clerk's `<PricingTable />` instead of the legacy plans grid. Pages that
  // need to be aware of subscription/usage (extra-render purchases, cancel
  // flow) will continue to live in stagery-api until D-11 wraps. Default `off`
  // keeps the existing UI for everyone.
  const useClerkBilling = shouldRenderClerkPricingTable();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [sub, setSub] = useState<SubStatus | null>(null);
  const [billing, setBilling] = useState<'yearly' | 'monthly'>('yearly');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [buyingRenders, setBuyingRenders] = useState(false);

  useEffect(() => {
    if (useClerkBilling) {
      // Clerk owns plans + subscription state — skip the legacy fetch.
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    // Fetch plans + subscription independently so a failure on one (e.g. user
    // has no active subscription, /payments/subscription 404) doesn't blank the
    // whole page. Each call defaults to a safe empty value on error.
    Promise.all([
      api.get<PlansResponse | Plan[]>('/api/payments/plans', { skipAuth: true }).catch((err: unknown) => {
        console.error('[SubscriptionPage] failed to load plans', err);
        return { currency: 'eur', plans: [] } as PlansResponse;
      }),
      api.get<SubStatus>('/api/payments/subscription').catch((err: unknown) => {
        console.error('[SubscriptionPage] failed to load subscription', err);
        return null;
      }),
    ])
      .then(([rawPlans, s]) => {
        if (cancelled) return;
        // Sprint 70 changed /api/payments/plans from Plan[] → { currency, plans: Plan[] }.
        // Unwrap the wrapped shape; fall back to treating the value as a bare array for
        // any older cached response still in flight.
        const planList: Plan[] = Array.isArray(rawPlans)
          ? rawPlans
          : Array.isArray((rawPlans as PlansResponse).plans)
            ? (rawPlans as PlansResponse).plans
            : [];
        setPlans(planList);
        setSub(s);
        if (planList.length === 0) {
          setLoadError('Kon abonnementsplannen niet laden. Probeer het later opnieuw.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [useClerkBilling]);

  const handleSubscribe = async (planId: string) => {
    setSubscribing(planId);
    try {
      const data = await api.post<{ checkoutUrl?: string; error?: string }>('/api/payments/subscribe', { plan: planId });
      if (data.checkoutUrl) {
        trackEvent('subscription_started', { plan: planId });
        window.location.href = data.checkoutUrl;
      } else {
        alert(data.error || 'Fout bij starten abonnement');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Fout bij starten abonnement');
    } finally {
      setSubscribing(null);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Weet je zeker dat je wilt opzeggen? Je kunt blijven renderen tot het einde van je huidige periode.')) return;
    setCancelling(true);
    try {
      const data = await api.post<{ ok?: boolean }>('/api/payments/cancel', {});
      if (data.ok) {
        setSub(prev => prev ? { ...prev, cancelled: true, active: false } : prev);
        trackEvent('subscription_cancelled');
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Opzeggen mislukt');
    } finally {
      setCancelling(false);
    }
  };

  const handleBuyRenders = async (amount: number) => {
    setBuyingRenders(true);
    try {
      const data = await api.post<{ checkoutUrl?: string }>('/api/payments/buy-renders', { amount });
      if (data.checkoutUrl) {
        trackEvent('extra_renders_purchased', { amount });
        window.location.href = data.checkoutUrl;
      }
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Fout bij kopen van extra renders');
    } finally {
      setBuyingRenders(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-ink/40">
        <Loader2 size={20} className="animate-spin mr-2" /> Laden…
      </div>
    );
  }

  if (useClerkBilling) {
    return (
      <div className="min-h-screen bg-bg">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="mb-10">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Abonnement</p>
            <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">
              Kies je plan
            </h1>
            <p className="text-sm text-ink/55 mt-2 max-w-xl">
              Voor minder dan één traditioneel gestylede foto per maand render je honderden ruimtes Funda-klaar.
            </p>
          </div>
          <ClerkPricingTable />
        </div>
      </div>
    );
  }

  const soloPlan = plans.find(p => p.label === 'Solo' && (billing === 'yearly' ? p.yearly : !p.yearly));
  const kantoorPlan = plans.find(p => p.label === 'Kantoor' && (billing === 'yearly' ? p.yearly : !p.yearly));

  const rendersPercent = sub?.active && sub.rendersPerMonth > 0
    ? Math.round((sub.rendersUsed / sub.rendersPerMonth) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">

        {/* Header */}
        <div className="mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink/45 mb-2">Abonnement</p>
          <h1 className="font-sans text-3xl md:text-4xl font-bold tracking-[-0.02em] text-ink">
            {sub?.active ? 'Jouw abonnement' : 'Kies je plan'}
          </h1>
          <p className="text-sm text-ink/55 mt-2 max-w-xl">
            Voor minder dan één traditioneel gestylede foto per maand render je honderden ruimtes Funda-klaar.
          </p>
        </div>

        {/* Huidig abonnement */}
        {sub?.active && (
          <div className="mb-10 rounded-2xl bg-white border border-black/8 p-6 md:p-7">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/45 mb-1">Huidig abonnement</p>
                <h2 className="font-sans text-xl md:text-2xl font-bold tracking-[-0.015em]">{sub.planLabel}</h2>
              </div>
              <span className="inline-flex items-center gap-1.5 bg-accent/10 text-accent border border-accent/25 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.06em]">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" /> Actief
              </span>
            </div>

            <div className="mb-5">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-ink">{sub.rendersUsed} / {sub.rendersPerMonth} renders</span>
                <span className="text-xs text-ink/50 font-medium">{rendersPercent}%</span>
              </div>
              <div className="w-full bg-ink/5 h-2 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${rendersPercent > 90 ? 'bg-red-500' : 'bg-accent'}`}
                  style={{ width: `${Math.min(rendersPercent, 100)}%` }} />
              </div>
              <p className="text-xs text-ink/50 mt-2">
                {sub.rendersPerMonth - sub.rendersUsed} renders over deze maand
                {sub.periodEnd && ` · vernieuwt ${new Date(sub.periodEnd).toLocaleDateString('nl-NL')}`}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-black/6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink/55 mr-2">Extra renders:</p>
              {[10, 25, 50].map(n => (
                <button key={n} onClick={() => handleBuyRenders(n)} disabled={buyingRenders}
                  className="inline-flex items-center gap-1.5 border border-black/10 hover:border-accent hover:text-accent px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50">
                  +{n}
                </button>
              ))}
              <button onClick={handleCancel} disabled={cancelling}
                className="ml-auto text-[11px] font-semibold uppercase tracking-[0.08em] text-ink/40 hover:text-red-500 transition-colors">
                {cancelling ? 'Opzeggen…' : 'Abonnement opzeggen'}
              </button>
            </div>
          </div>
        )}

        {loadError && (
          <div className="mb-8 rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-900">
            {loadError}
          </div>
        )}

        {sub?.cancelled && (
          <div className="mb-10 rounded-xl bg-amber-50 border border-amber-200 px-5 py-4 text-sm text-amber-900">
            Je abonnement is opgezegd. Renders werken tot {sub.periodEnd ? new Date(sub.periodEnd).toLocaleDateString('nl-NL') : 'einde periode'}.
          </div>
        )}

        {/* Billing toggle */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-sans text-xl md:text-2xl font-bold tracking-[-0.015em] text-ink">
              {sub?.active ? 'Upgrade of downgrade' : 'Plannen'}
            </h2>
          </div>
          <div className="inline-flex items-center bg-ink/5 rounded-full p-1">
            <button onClick={() => setBilling('yearly')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${billing === 'yearly' ? 'bg-white text-ink shadow-sm' : 'text-ink/50 hover:text-ink'}`}>
              Jaarlijks <span className="text-accent ml-1 font-bold">-25%</span>
            </button>
            <button onClick={() => setBilling('monthly')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${billing === 'monthly' ? 'bg-white text-ink shadow-sm' : 'text-ink/50 hover:text-ink'}`}>
              Maandelijks
            </button>
          </div>
        </div>

        {/* Plans grid - 3 columns: Gratis, Solo (featured), Kantoor */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-5 mb-8 items-stretch">

          {/* Gratis */}
          <PlanCard
            icon={<Sparkles size={16} />}
            name="Gratis"
            price="€0"
            priceSub="/ maand"
            description="Test de software zonder risico. Geen creditcard."
            features={[
              { label: 'Renders per maand', value: '10' },
              { label: 'Varianten per render', value: '1 stijl' },
              { label: 'Feedback-rondes', value: 'Onbeperkt' },
              { label: 'Presentatielinks', value: 'Onbeperkt' },
              { label: 'Support', value: 'Community' },
            ]}
            cta={!sub?.active ? { label: 'Je huidige plan', disabled: true } : undefined}
          />

          {/* Solo - featured */}
          {soloPlan && (
            <PlanCard
              featured
              tag="Populair"
              icon={<Zap size={16} />}
              name="Solo"
              price={`€${Number(soloPlan.amount).toFixed(0)}`}
              priceSub="/ maand"
              priceNote={billing === 'yearly' ? 'bij jaarlijkse betaling' : undefined}
              description="Voor de zelfstandige makelaar of klein kantoor."
              features={[
                { label: 'Renders per maand', value: String(soloPlan.renderQuota ?? soloPlan.renders ?? '') },
                { label: 'Varianten per render', value: '3 stijlen' },
                { label: 'Feedback-rondes', value: 'Onbeperkt' },
                { label: 'Presentatielinks', value: 'Onbeperkt' },
                { label: 'Support', value: 'E-mail' },
              ]}
              cta={{
                label: sub?.plan === soloPlan.id ? 'Huidig plan' : 'Start met Solo',
                onClick: () => handleSubscribe(soloPlan.id),
                loading: subscribing === soloPlan.id,
                disabled: sub?.plan === soloPlan.id,
                isCurrent: sub?.plan === soloPlan.id,
              }}
            />
          )}

          {/* Kantoor */}
          {kantoorPlan && (
            <PlanCard
              icon={<Building2 size={16} />}
              name="Kantoor"
              price={`€${Number(kantoorPlan.amount).toFixed(0)}`}
              priceSub="/ maand"
              priceNote={billing === 'yearly' ? 'bij jaarlijkse betaling' : undefined}
              description="Voor kantoren met een heel team makelaars."
              features={[
                { label: 'Renders per maand', value: String(kantoorPlan.renderQuota ?? kantoorPlan.renders ?? '') },
                { label: 'Varianten per render', value: '3 stijlen' },
                { label: 'Team seats', value: '10' },
                { label: 'Resolutie', value: 'Hi-res (print)' },
                { label: 'Support', value: 'Prioriteit · Slack' },
              ]}
              cta={{
                label: sub?.plan === kantoorPlan.id ? 'Huidig plan' : 'Start met Kantoor',
                onClick: () => handleSubscribe(kantoorPlan.id),
                loading: subscribing === kantoorPlan.id,
                disabled: sub?.plan === kantoorPlan.id,
                isCurrent: sub?.plan === kantoorPlan.id,
              }}
            />
          )}
        </div>

        {/* Kosten-vergelijking */}
        <div className="rounded-2xl bg-white border border-black/8 p-5 md:p-6 mb-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-ink/45 mb-3">Wat kost 1 foto?</p>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <p className="text-xs text-ink/55 mb-1">Traditionele stager</p>
              <p className="font-sans text-lg font-bold text-ink">€30 – €50</p>
            </div>
            <div>
              <p className="text-xs text-ink/55 mb-1">Stagery Solo</p>
              <p className="font-sans text-lg font-bold text-accent">€0,53</p>
            </div>
            <div>
              <p className="text-xs text-ink/55 mb-1">Besparing</p>
              <p className="font-sans text-lg font-bold text-ink">95%+</p>
            </div>
          </div>
        </div>

        {/* Gratis credits info */}
        {user.credits > 0 && (
          <div className="rounded-xl bg-accent/5 border border-accent/15 px-5 py-4 text-center mb-8">
            <p className="text-sm text-ink/70">
              Je hebt nog <strong className="text-accent font-bold">{user.credits} gratis credits</strong>. Die worden eerst gebruikt voordat je abonnement-renders worden afgeschreven.
            </p>
          </div>
        )}

        {/* Waarom niet gratis */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-accent/8 via-accent/4 to-white border border-accent/20 p-6 md:p-8">
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-accent/10 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
            <div className="shrink-0">
              <div className="w-12 h-12 rounded-xl bg-accent text-white flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(241,110,0,0.5)]">
                <Sparkles size={22} />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="font-sans text-lg md:text-xl font-bold tracking-[-0.015em] text-ink mb-1">
                Waarom is Stagery niet gratis?
              </h2>
              <p className="text-sm text-ink/70 leading-relaxed mb-4 max-w-2xl">
                Achter elke restyling draait zware AI-infrastructuur: beeldmodellen van Google en OpenAI,
                GPU-compute in de cloud en zorgvuldig getrainde prompts die zorgen voor Funda-waardige
                resultaten. Voor elke render betalen wij kosten. Jouw credits dekken die AI-rekening
                plus onze hosting, opslag en support.
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <WhyItem
                  icon={<Cpu size={14} />}
                  title="AI-compute"
                  body="Per foto draait het model enkele seconden op dure GPU's."
                />
                <WhyItem
                  icon={<ShieldCheck size={14} />}
                  title="Kwaliteit & support"
                  body="Wij trainen prompts bij, monitoren kwaliteit en helpen je met feedback."
                />
                <WhyItem
                  icon={<ImageIcon size={14} />}
                  title="Opslag & presentatie"
                  body="Jouw renders blijven bewaard en zijn direct deelbaar met kopers."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhyItem: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({ icon, title, body }) => (
  <div className="rounded-xl bg-white/70 border border-black/6 p-3.5">
    <div className="flex items-center gap-2 mb-1">
      <span className="text-accent">{icon}</span>
      <p className="text-xs font-bold text-ink">{title}</p>
    </div>
    <p className="text-[11.5px] text-ink/60 leading-snug">{body}</p>
  </div>
);

// ---------- PlanCard sub-component ----------

interface Feature { label: string; value: string }
interface CtaSpec { label: string; onClick?: () => void; loading?: boolean; disabled?: boolean; isCurrent?: boolean }

const PlanCard: React.FC<{
  featured?: boolean;
  tag?: string;
  icon: React.ReactNode;
  name: string;
  price: string;
  priceSub: string;
  priceNote?: string;
  description: string;
  features: Feature[];
  cta?: CtaSpec;
}> = ({ featured, tag, icon, name, price, priceSub, priceNote, description, features, cta }) => (
  <div className={`relative rounded-2xl border flex flex-col p-6 md:p-7 transition-all ${
    featured
      ? 'bg-white border-accent/40 shadow-[0_20px_48px_-24px_rgba(241,110,0,0.35)] md:-my-2'
      : 'bg-white border-black/8 hover:border-black/15'
  }`}>
    {tag && (
      <span className="absolute -top-3 right-6 bg-accent text-white px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.08em] shadow-[0_6px_16px_-6px_rgba(241,110,0,0.5)]">
        {tag}
      </span>
    )}
    <div className="flex items-center gap-2 mb-2">
      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${featured ? 'bg-accent text-white' : 'bg-ink/5 text-ink/60'}`}>{icon}</span>
      <h3 className="font-sans text-base font-bold tracking-[-0.015em] text-ink">{name}</h3>
    </div>
    <div className="flex items-baseline gap-1.5 mb-1">
      <span className="font-sans text-4xl md:text-5xl font-bold tracking-[-0.03em] text-ink tabular-nums">{price}</span>
      <span className="text-sm text-ink/55 font-medium">{priceSub}</span>
    </div>
    {priceNote && <p className="text-[11px] text-accent font-semibold mb-2">{priceNote}</p>}
    <p className="text-sm text-ink/55 mb-5 leading-snug">{description}</p>

    <ul className="border-t border-black/8 pt-4 mb-6 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-center justify-between gap-2 py-2 text-sm border-b border-black/5 last:border-b-0">
          <span className="text-ink/65">{f.label}</span>
          <span className="text-ink font-semibold tabular-nums">{f.value}</span>
        </li>
      ))}
    </ul>

    {cta && (
      <button
        onClick={cta.onClick}
        disabled={cta.disabled || cta.loading}
        className={`w-full py-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
          cta.isCurrent
            ? 'bg-ink/5 text-ink/50 cursor-default'
            : featured
            ? 'bg-accent hover:bg-accent-hover text-white shadow-[0_6px_18px_-6px_rgba(241,110,0,0.5)] hover:shadow-[0_10px_26px_-8px_rgba(241,110,0,0.55)] hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0'
            : 'bg-ink text-white hover:bg-ink/85 disabled:opacity-40'
        }`}
      >
        {cta.loading ? <Loader2 size={14} className="animate-spin" /> : !cta.isCurrent && <CreditCard size={14} />}
        {cta.label}
      </button>
    )}
  </div>
);
